import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';
import EmptyState from './EmptyState';
import StatsCards from './StatsCards';
import SearchBar from './SearchBar';
import MoodFilter from './MoodFilter';
import EntryCard from './EntryCard';
import DeleteDialog from './DeleteDialog';
import './Dashboard.css';

function Dashboard({ entries, onDeleteEntry, onOpenWrite, onOpenModal, user, todayEntry, onEditEntry }) {
  const navigate = useNavigate();
  const {
    searchQuery,
    setSearchQuery,
    filterMood,
    setFilterMood,
    deleteConfirm,
    setDeleteConfirm,
    filterOpen,
    setFilterOpen,
    isMobile,
    loading,
    loadingMore,
    filteredEntries,
    stats,
    hasMore,
    loadMoreEntries,
    removeEntryFromStorage
  } = useDashboard(entries);

  const handleDeleteClick = (entryId) => {
    setDeleteConfirm(entryId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      // Remove from sessionStorage before deleting
      removeEntryFromStorage(deleteConfirm);
      onDeleteEntry(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleMoodSelect = (value) => {
    setFilterMood(value);
    setFilterOpen(false);
  };

  const handleOpenInDiary = (entry) => {
    // Find the index of this entry in the filteredEntries array (which includes all loaded entries)
    const entryIndex = filteredEntries.findIndex(e => (e.id || e.entryId) === (entry.id || entry.entryId));
    if (entryIndex !== -1) {
      navigate(`/diary?page=${entryIndex}`);
    }
  };


  // Fullscreen welcome when no entries
  if (entries.length === 0) {
    return (
      <main className={`dashboard dashboard--welcome ${isMobile ? 'dashboard--welcome-mobile' : 'dashboard--welcome-desktop'}`}>
        <div className="dashboard__container">
          <EmptyState 
            isMobile={isMobile}
            onOpenWrite={onOpenWrite}
          />
        </div>
      </main>
    );
  }

  // Regular dashboard with entries
  return (
    <main className="dashboard">
      <div className="dashboard__container">
        {/* Header with Action Buttons */}
        <div className="dashboard__top-bar">
          <div className="dashboard__greeting">
            <h1 className="dashboard__title">Your Journal</h1>
            <p className="dashboard__subtitle">A space for your thoughts</p>
          </div>
          
          <div className="dashboard__actions">
            <Link 
              to="/diary"
              className="dashboard__diary-btn"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              <span>Read diary</span>
            </Link>

            <Link 
              to={todayEntry ? `/write/${todayEntry.id || todayEntry.entryId}` : '/write'}
              className={`dashboard__write-btn ${todayEntry ? 'dashboard__write-btn--edit' : ''}`}
              title={todayEntry ? "Edit today's entry" : "Write a new entry"}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>{todayEntry ? "Today's Entry" : "New Entry"}</span>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <StatsCards 
          totalEntries={stats.totalEntries}
          thisWeek={stats.thisWeek}
          topMood={stats.topMood}
        />

        {/* Search and Filter Bar */}
        <div className="dashboard__controls">
          <SearchBar 
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            loading={loading}
          />
          
          <MoodFilter 
            filterMood={filterMood}
            filterOpen={filterOpen}
            onToggle={() => setFilterOpen(!filterOpen)}
            onSelect={handleMoodSelect}
          />
        </div>

        {/* Entries or No Results */}
        {filteredEntries.length === 0 ? (
          <div className="dashboard__no-results">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <p>No entries match your search</p>
          </div>
        ) : (
          <>
            <div className="dashboard__entries">
              {filteredEntries.map((entry, index) => {
                const isToday = todayEntry && entry.id === todayEntry.id;
                return (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    isToday={isToday}
                    index={index}
                    onEdit={onEditEntry}
                    onDelete={handleDeleteClick}
                    onOpenInDiary={handleOpenInDiary}
                  />
                );
              })}
            </div>
            
            {/* Load More Button */}
            {hasMore && (
              <div className="dashboard__load-more-container">
                <button
                  className="dashboard__load-more-btn"
                  onClick={loadMoreEntries}
                  disabled={loadingMore || loading}
                >
                  {loadingMore ? (
                    <>
                      <div className="dashboard__loading-spinner"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      <span>Load More</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* End Message */}
            {!hasMore && filteredEntries.length > 0 && (
              <div className="dashboard__end-message">
                <p>You've reached the end of your entries</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <DeleteDialog 
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </main>
  );
}

export default Dashboard;
